import { isEmpty, sortBy } from 'lodash-es'
import PropTypes from 'prop-types'
import { useMemo, useState } from 'react'
import { useMediaQuery } from 'react-responsive'
import ReactTooltip from 'react-tooltip'
import showdown from 'showdown'
import showdownHighlight from 'showdown-highlight'
import showdownKatex from 'showdown-katex'
import styled from 'styled-components'
import { fileIsInScope } from '../../../../utils'
import arrowLeftIcon from '../../assets/images/arrow-left-white.svg'
import printIcon from '../../assets/images/print-sharp-regular.svg'
import {
  FINDING_HTML_ID_ATTRIBUTE,
  OTHER_TITLE_ID,
  SUGGESTION_HTML_ID_ATTRIBUTE,
} from '../../constants'
import Badge from '../Badge'
import Footer from '../Footer'
import Navbar from '../Navbar'
import Sidebar from '../Sidebar'
import GlobalStyle from './GlobalStyle'
import AboutQuantstamp from './sections/AboutQuantstamp'
import AdherenceToBestPractices from './sections/AdherenceToBestPractices'
import AdherenceToSpecification from './sections/AdherenceToSpecification'
import Appendix from './sections/Appendix'
import AssessmentBreakdown from './sections/AssessmentBreakdown'
import AutomatedAnalysis from './sections/AutomatedAnalysis'
import Changelog from './sections/Changelog'
import CodeCoverage from './sections/CodeCoverage'
import CodeDocumentation from './sections/CodeDocumentation'
import Definitions from './sections/Definitions'
import ExecutiveSummary from './sections/ExecutiveSummary'
import Findings from './sections/Findings'
import KeyActorsCapabilities from './sections/KeyActorsAndCapabilities'
import OperationalConsiderations from './sections/OperationalConsiderations'
import OperationalSecurityChecklists from './sections/OperationalSecurityChecklists'
import Scope from './sections/Scope'
import AuditorSuggestions from './sections/Suggestions'
import SummaryOfFindings from './sections/SummaryOfFindings'
import TestSuiteResults from './sections/TestSuiteResults'
import Toolset from './sections/Toolset'
import { getStatusLabelType, hasTools, qsBadge, qsCodeBlockStyle, qsFindingId } from './utils'

export const convertMdToHtml = new showdown.Converter({
  tables: true,
  literalMidWordUnderscores: true,
  disableForced4SpacesIndentedSublists: true,
  extensions: [
    showdownHighlight({
      pre: true,
      auto_detection: true,
    }),
    qsCodeBlockStyle,
    qsBadge,
    showdownKatex({
      throwOnError: true,
      displayMode: false,
      errorColor: '#1500ff',
    }),
  ],
  emoji: true,
})

const splitFiles = value =>
  typeof value === 'string'
    ? value
        .split(',')
        .map(item => item.trim())
        .filter(Boolean)
    : []

const Container = styled.div`
  @media screen and (max-width: 1200px) {
    .sidebar {
      display: none;
    }
  }
`

const Header = styled.header`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 26px 40px;
  background: #1a2f77;

  @media screen and (max-width: 1200px) {
    padding: 20px;
  }
`

const HeaderTitle = styled.h1`
  word-break: break-all;
  margin: 0;
  font-weight: 700;
  font-size: 24px;
  line-height: 58px;
  text-transform: capitalize;
  color: white;

  @media screen and (max-width: 1200px) {
    padding: 20px;
    font-weight: 700;
    font-size: 20px;
    line-height: 28px;
  }

  @media screen and (min-width: 1200px) {
    position: relative;
    right: -350px;
  }
`

const DownloadButton = styled.div`
  font-family: 'SohneMono';
  font-size: 14px;
  line-height: 20px;
  color: white;
  cursor: pointer;

  div {
    display: flex;
    gap: 8px;
    padding: 8px 16px;
  }
`

const BackButton = styled.a`
  position: absolute;
  left: 21px;
  font-family: 'SohneMono';
  font-size: 14px;
  line-height: 20px;
  color: white;
  cursor: pointer;
  padding: 8px 16px;

  div {
    display: flex;
    gap: 8px;
  }

  &:hover {
    background: rgba(255, 255, 255, 0.1);
    text-decoration: none;
  }

  @media screen and (max-width: 1200px) {
    display: none;
  }

  @media print {
    display: none;
  }
`

const Body = styled.div`
  display: flex;
  background-color: white;

  .sidebar {
    flex: 1;
  }
`

const Content = styled.div`
  flex: 2;
  max-width: ${({ fullWidth }) => (fullWidth ? '100%' : '1240px')};
  overflow: overlay;
  z-index: 4;
`

const WebReport = ({ report, loading }) => {
  const [downloading, setDownloading] = useState(false)
  const isTabletOrMobile = useMediaQuery({ query: '(max-width: 1200px)' })
  const hideSidebar = new URLSearchParams(window.location.search).get('sidebar') === 'false'
  convertMdToHtml.addExtension(
    qsFindingId(report?.reportContent?.breakdownAndAutomatedAnalysis?.vulnIndex || 'QS'),
  )

  const sections = useMemo(() => {
    const findings = (report?.reportContent?.breakdownAndAutomatedAnalysis?.sortBySeverity
      ? sortBy(report?.reportContent?.breakdownAndAutomatedAnalysis?.vulnerabilitiesArray, v =>
          parseInt(v.severity.value, 10),
        )
      : report?.reportContent?.breakdownAndAutomatedAnalysis?.vulnerabilitiesArray
    )?.map((vulnerability, index) => {
      const findingId = index + 1
      const prefix = report?.reportContent?.breakdownAndAutomatedAnalysis?.vulnIndex || 'QS'

      return {
        to: `#${FINDING_HTML_ID_ATTRIBUTE}${findingId}`,
        label: (
          <>
            <b>
              {prefix}-{findingId}
            </b>{' '}
            {Number(vulnerability.title.value) !== OTHER_TITLE_ID
              ? vulnerability.title.label
              : vulnerability.otherTitle}
            &nbsp;&nbsp;
            {vulnerability.status ? (
              <Badge type={getStatusLabelType(vulnerability.status.value)}>
                {vulnerability.status.label}
              </Badge>
            ) : null}
          </>
        ),
      }
    })
    const suggestions = report?.reportContent?.breakdownAndAutomatedAnalysis?.suggestionsArray?.map(
      (vulnerability, index) => {
        const findingId = index + 1

        return {
          to: `#${SUGGESTION_HTML_ID_ATTRIBUTE}${findingId}`,
          label: (
            <>
              <b>S{findingId}</b>{' '}
              {vulnerability.title.label !== 'Other'
                ? vulnerability.title.label
                : vulnerability.otherTitle}
              &nbsp;&nbsp;
              {vulnerability.status ? (
                <Badge type={getStatusLabelType(vulnerability.status.value)}>
                  {vulnerability.status.label}
                </Badge>
              ) : null}
            </>
          ),
        }
      },
    )

    const repoNames = Object.keys(report?.reportContent?.filesAndScope?.repos || [])
    const repoFiles = repoNames
      .map(repoName => {
        const repo = report?.reportContent?.filesAndScope?.repos[repoName]
        if (!repo || repo.hidden) {
          return undefined
        }

        const includePaths = splitFiles(repo.includePaths)
        const excludePaths = splitFiles(repo.excludePaths)
        const extensions = splitFiles(repo.extensions)

        return {
          includePaths,
          excludePaths,
          extensions,
          showExcluded: repo.showExcluded,
          hideIncluded: repo.hideIncluded,
          name: repo?.name || repoName,
          useFileNameOnly: repo.useFileNameOnly,
          files: repo.files || [],
          filesInScope: (repo.files || []).filter(file =>
            fileIsInScope(file.name || file, includePaths, excludePaths, extensions),
          ),
        }
      })
      .filter(repo => repo?.name && repo.filesInScope.length)
    if (report?.reportContent) {
      report.reportContent.repoFiles = repoFiles
    }

    return [
      {
        to: '#executive-summary',
        label: 'Executive Summary',
        component: ExecutiveSummary,
      },
      {
        to: '#summary-of-findings',
        label: 'Summary of Findings',
        component: SummaryOfFindings,
      },
      {
        to: '#assessment-breakdown',
        label: 'Assessment Breakdown',
        component: AssessmentBreakdown,
      },
      {
        to: '#scope',
        label: 'Scope',
        component: Scope,
        invisible:
          isEmpty(report?.reportContent?.summary?.scope?.description) &&
          isEmpty(report?.reportContent?.summary?.scope?.filesIncluded) &&
          isEmpty(report?.reportContent?.summary?.scope?.filesExcluded) &&
          isEmpty(report?.reportContent?.repoFiles),
      },
      {
        to: '#operational-considerations',
        label: 'Operational Considerations',
        component: OperationalConsiderations,
        invisible: isEmpty(report?.reportContent?.summary?.operationalConsiderations),
      },
      {
        to: '#key-actors-and-their-capabilities',
        label: 'Key Actors and Capabilities',
        component: KeyActorsCapabilities,
        invisible: isEmpty(report?.reportContent?.summary?.keyActorsCapabilities),
      },
      {
        to: '#operational-security-checklists',
        label: 'Operational Security Checklists',
        component: OperationalSecurityChecklists,
        invisible: isEmpty(report?.reportContent?.summary?.operationalSecurityChecklists),
      },
      {
        to: '#findings',
        label: `Findings ${isTabletOrMobile ? `(${findings?.length})` : ''}`,
        subOptions: findings,
        component: Findings,
        invisible: !findings?.length,
      },
      {
        to: '#auditor-suggestions',
        label: `Auditor Suggestions ${isTabletOrMobile ? `(${suggestions?.length})` : ''}`,
        subOptions: suggestions,
        component: AuditorSuggestions,
        invisible: isEmpty(report?.reportContent?.breakdownAndAutomatedAnalysis?.suggestionsArray),
      },
      {
        to: '#code-documentation',
        label: 'Code Documentation',
        component: CodeDocumentation,
        invisible: isEmpty(report?.reportContent?.breakdownAndAutomatedAnalysis?.docReviewText),
      },
      {
        to: '#adherence-to-best-practices',
        label: 'Adherence to Best Practices',
        component: AdherenceToBestPractices,
        invisible: isEmpty(report?.reportContent?.breakdownAndAutomatedAnalysis?.bestPracticesText),
      },
      {
        to: '#adherence-to-specification',
        label: 'Adherence to Specification',
        component: AdherenceToSpecification,
        invisible: isEmpty(report?.reportContent?.breakdownAndAutomatedAnalysis?.specReviewText),
      },
      {
        label: 'Definitions',
        component: Definitions,
      },
      {
        to: '#appendix',
        label: 'Appendix',
        component: Appendix,
        invisible:
          isEmpty(report?.reportContent?.breakdownAndAutomatedAnalysis?.contractHashes) &&
          isEmpty(report?.reportContent?.breakdownAndAutomatedAnalysis?.testsHashes) &&
          isEmpty(report?.reportContent?.repoFiles),
      },
      {
        to: '#toolset',
        label: 'Toolset',
        component: Toolset,
        invisible: isEmpty(report?.reportContent?.breakdownAndAutomatedAnalysis?.tools),
      },
      {
        to: '#automated-analysis',
        label: 'Automated Analysis',
        component: AutomatedAnalysis,
        invisible: !hasTools(report?.reportContent?.breakdownAndAutomatedAnalysis?.tools),
      },
      {
        to: '#test-suite-results',
        label: 'Test Suite Results',
        component: TestSuiteResults,
        invisible:
          isEmpty(report?.reportContent?.breakdownAndAutomatedAnalysis?.testSuiteText) &&
          isEmpty(report?.reportContent?.breakdownAndAutomatedAnalysis?.testSuiteData),
      },
      {
        to: '#code-coverage',
        label: 'Code Coverage',
        component: CodeCoverage,
        invisible:
          isEmpty(report?.reportContent?.breakdownAndAutomatedAnalysis?.codeCoverageSummary) &&
          isEmpty(report?.reportContent?.breakdownAndAutomatedAnalysis?.codeCoverageOutput),
      },
      {
        to: '#changelog',
        label: 'Changelog',
        component: Changelog,
      },
      {
        to: '#about-quantstamp',
        label: 'About Quantstamp',
        component: AboutQuantstamp,
      },
    ]
  }, [
    report?.reportContent?.breakdownAndAutomatedAnalysis?.vulnerabilitiesArray,
    report?.reportContent?.breakdownAndAutomatedAnalysis?.docReviewText,
    report?.reportContent?.breakdownAndAutomatedAnalysis?.specReviewText,
    report?.reportContent?.breakdownAndAutomatedAnalysis?.specReviewText,
    report?.reportContent?.breakdownAndAutomatedAnalysis?.codeCoverageSummary,
    report?.reportContent?.breakdownAndAutomatedAnalysis?.codeCoverageOutput,
    report?.reportContent?.breakdownAndAutomatedAnalysis?.testSuiteText,
    report?.reportContent?.breakdownAndAutomatedAnalysis?.testSuiteData,
    report?.reportContent?.breakdownAndAutomatedAnalysis?.contractHashes,
    report?.reportContent?.breakdownAndAutomatedAnalysis?.testsHashes,
    isTabletOrMobile,
  ])

  const handleDownload = async () => {
    setDownloading(true)

    await new Promise(resolve => setTimeout(resolve, 800))
    window.print()

    setDownloading(false)
  }

  const dynamicNavOptions = sections.filter(sec => !sec.invisible && !!sec.to)

  return (
    <Container>
      <GlobalStyle />
      {loading ? (
        <>Loading</>
      ) : (
        <>
          <Navbar
            options={
              !downloading
                ? isTabletOrMobile
                  ? dynamicNavOptions
                  : [
                      {
                        to: 'https://certificate.quantstamp.com',
                        label: 'Browse public security assessments',
                      },
                      {
                        to: 'https://quantstamp.com/audits',
                        label: 'Request a security audit',
                      },
                      {
                        to: 'https://quantstamp.com/about',
                        label: 'About',
                      },
                      {
                        to: 'mailto:audit@quantstamp.com',
                        label: 'Contact',
                      },
                    ]
                : []
            }
            draft={!report?.reportContent?.completed}
          />
          <Header>
            <BackButton href="https://certificate.quantstamp.com/">
              <div>
                <img src={arrowLeftIcon} alt="Browse public reports" /> Browse public reports
              </div>
            </BackButton>
            <HeaderTitle>{report.reportContent.setup.projectName}</HeaderTitle>
            {!downloading && (
              <>
                <DownloadButton
                  onClick={handleDownload}
                  data-for="recommended-settings"
                  data-tip=""
                >
                  <div>
                    Print <img src={printIcon} alt="Download or print" />
                  </div>
                </DownloadButton>
                <ReactTooltip
                  id="recommended-settings"
                  getContent={() =>
                    'Recommended settings: Portrait, A3, no margin, and "Show background graphics" checked.'
                  }
                />
              </>
            )}
          </Header>
          <Body>
            {!downloading && !hideSidebar && <Sidebar options={dynamicNavOptions} />}
            <Content fullWidth={hideSidebar}>
              {sections.map(section => {
                return section.invisible ? (
                  <></>
                ) : (
                  <section.component report={report} key={section.to} />
                )
              })}
              <Footer projectName={report.reportContent.setup.projectName} />
            </Content>
          </Body>
        </>
      )}
    </Container>
  )
}

WebReport.propTypes = {
  report: PropTypes.object,
  loading: PropTypes.bool,
}

export default WebReport
